import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CHIEF_APOSTLE, SECRETARY, COUNCIL } from "../data/content";

function LeaderLetter({ leader, align = "left" }) {
  const imgFirst = align === "left";
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
      <Reveal className={`md:col-span-5 ${imgFirst ? "md:order-1" : "md:order-2"}`}>
        <div className="relative">
          <div className="absolute -inset-3 border border-secondary/40" />
          <img
            src={leader.image}
            alt={leader.name}
            className="relative w-full aspect-[4/5] object-cover grayscale contrast-125"
            data-testid="leader-portrait"
          />
        </div>
        <div className="mt-6">
          <p className="font-display uppercase text-2xl text-foreground">{leader.name}</p>
          <p className="overline text-secondary mt-2">{leader.title}</p>
        </div>
      </Reveal>

      <Reveal delay={0.15} className={`md:col-span-7 ${imgFirst ? "md:order-2" : "md:order-1"}`}>
        <p className="font-editorial italic text-3xl md:text-5xl text-foreground leading-tight mb-10">
          “{leader.pull}”
        </p>
        <div className="space-y-5">
          {leader.letter.map((p, i) => (
            <p key={i} className="font-body text-muted-foreground leading-relaxed">
              {p}
            </p>
          ))}
        </div>
        <p className="font-editorial italic text-xl text-secondary mt-8">{leader.sign}</p>
        <p className="font-display uppercase text-lg text-foreground mt-1">{leader.name}</p>
      </Reveal>
    </div>
  );
}

export default function Leadership() {
  return (
    <div data-testid="leadership-page">
      <PageHeader
        overline="The Apostolic Government"
        lines={["Leadership"]}
        sub="Welcome from those who steward the vision of the Network."
        image={CHIEF_APOSTLE.image}
      />

      <section className="px-6 md:px-10 max-w-[1600px] mx-auto pb-28">
        <Tabs defaultValue="chief" className="w-full">
          <TabsList
            className="bg-transparent border-b border-border w-full justify-start rounded-none h-auto p-0 gap-8 mb-16"
            data-testid="leadership-tabs"
          >
            <TabsTrigger
              value="chief"
              data-testid="tab-chief-apostle"
              className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground px-0 pb-4 overline data-[state=active]:shadow-none"
            >
              Chief Apostle
            </TabsTrigger>
            <TabsTrigger
              value="secretary"
              data-testid="tab-secretary-general"
              className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground px-0 pb-4 overline data-[state=active]:shadow-none"
            >
              General Secretary
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chief">
            <LeaderLetter leader={CHIEF_APOSTLE} align="left" />
          </TabsContent>
          <TabsContent value="secretary">
            <LeaderLetter leader={SECRETARY} align="right" />
          </TabsContent>
        </Tabs>
      </section>

      {/* COUNCIL */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto pb-32" data-testid="apostolic-council">
        <Reveal className="mb-12">
          <p className="overline text-primary mb-4">The Roster</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-foreground">
            Apostolic Council & Leadership
          </h2>
        </Reveal>
        <div className="border-t border-border">
          {COUNCIL.map((c, i) => (
            <motion.article
              key={c.name}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 6) * 0.05 }}
              data-testid="council-row"
              className="group grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-6 md:gap-8 items-start py-8 border-b border-border transition-all duration-300 hover:bg-muted/20"
            >
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-muted ring-1 ring-secondary/30 shrink-0">
                {c.image ? (
                  <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="font-display uppercase text-secondary text-xl">
                      {c.name.split(" ").slice(-1)[0].charAt(0)}
                    </span>
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <h3 className="font-editorial text-2xl md:text-4xl text-foreground group-hover:text-secondary transition-colors">
                  {c.name}
                </h3>
                {c.bio && (
                  <p className="font-body text-sm md:text-base text-muted-foreground mt-3 leading-relaxed max-w-3xl">
                    {c.bio}
                  </p>
                )}
              </div>
              <span className="overline text-muted-foreground text-[0.65rem] md:text-right whitespace-nowrap">{c.role}</span>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
